import React, { useState } from 'react';
import { FaUserPlus, FaExclamationTriangle, FaCheck } from 'react-icons/fa';
import UserAutoSuggest from './UserAutoSuggest.jsx';
import LoadingSpinner from './LoadingSpinner.jsx';
import { useT } from '../../contexts/LocaleContext.jsx';
import '../../styles/partial/AddModeratorForm.css';

/**
 * Finds one of the band's followers (ENG-473) and adds them as a moderator.
 * `onAddModerator(userId)` resolves to `{ success, error }`, where `error` is
 * `{ text }` (the server's wording) or `{ key }` (a catalog key), as
 * useModerators returns it.
 */
const AddModeratorForm = ({
  onAddModerator,
  existingModerators = [],
  bandMembers = [],
  maxModerators = 10,
  className = ''
}) => {
  const t = useT();
  const [loading, setLoading] = useState(false);
  // `{ text } | { key } | { key, params }` of the last failure, or null.
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const moderatorIdOf = (mod) => mod.UserID ?? mod.user_id;
  const memberIdOf = (member) => member.UserID ?? member.user_id;

  // Get list of user IDs to exclude (existing moderators + band members)
  const excludeUserIds = React.useMemo(() => {
    const moderatorIds = existingModerators.map(moderatorIdOf);
    const memberIds = bandMembers.map(memberIdOf);
    return [...moderatorIds, ...memberIds].filter((id) => id != null);
  }, [existingModerators, bandMembers]);

  const describe = (failure) => (failure.key ? t(failure.key, failure.params) : failure.text);

  const handleUserSelect = async (user) => {
    const userId = user.id;
    const username = user.username;

    // Clear previous messages
    setError(null);
    setSuccess('');

    // Check if already at max limit
    if (existingModerators.length >= maxModerators) {
      setError({ key: 'moderators.add.limitError', params: { max: maxModerators } });
      return;
    }

    // Double check if user is already a moderator (shouldn't happen with exclusion)
    if (existingModerators.some(mod => String(moderatorIdOf(mod)) === String(userId))) {
      setError({ key: 'moderators.add.alreadyModerator', params: { username } });
      return;
    }

    // Check if user is a band member (shouldn't happen with exclusion)
    if (bandMembers.some(member => String(memberIdOf(member)) === String(userId))) {
      setError({ key: 'moderators.add.isMember', params: { username } });
      return;
    }

    setLoading(true);

    try {
      const result = await onAddModerator(userId);

      if (result.success) {
        setSuccess(username);
        setSearchValue('');
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || { key: 'moderators.errors.addFailed' });
      }
    } catch (err) {
      console.error('Error adding moderator:', err);
      setError({ key: 'moderators.errors.addFailed' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    // Clear messages when user starts typing
    if (error) setError(null);
    if (success) setSuccess('');
  };

  const isAtMaxLimit = existingModerators.length >= maxModerators;

  return (
    <div className={`add-moderator-form ${className}`}>
      <div className="add-moderator-form-header">
        <h3>
          <FaUserPlus aria-hidden="true" /> {t('moderators.add.title')}
        </h3>
        {isAtMaxLimit && (
          <div className="add-moderator-form-warning">
            <FaExclamationTriangle aria-hidden="true" />
            <span>{t('moderators.add.limitReached', { max: maxModerators })}</span>
          </div>
        )}
      </div>

      {!isAtMaxLimit && (
        <div className="add-moderator-form-content">
          <div className="add-moderator-form-description">
            <p>{t('moderators.add.description')}</p>
          </div>

          <div className="add-moderator-form-search">
            <UserAutoSuggest
              value={searchValue}
              onChange={handleSearchChange}
              onUserSelect={handleUserSelect}
              excludeUserIds={excludeUserIds}
              className="add-moderator-form-input"
              disabled={loading}
            />
          </div>

          {loading && (
            <div className="add-moderator-form-loading">
              <LoadingSpinner size="small" />
              <span>{t('moderators.add.adding')}</span>
            </div>
          )}

          {error && (
            <div className="add-moderator-form-error" role="alert">
              <FaExclamationTriangle aria-hidden="true" />
              <span>{t('moderators.add.error', { error: describe(error) })}</span>
            </div>
          )}

          {success && (
            <div className="add-moderator-form-success" role="status">
              <FaCheck aria-hidden="true" />
              <span>{t('moderators.add.added', { username: success })}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddModeratorForm;
